'use client'

import classnames from 'classnames'
import styles from './AllergenGuide.module.scss'
import ContentWithStickyNavigation from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'
import Widont from '@/components/Widont/Widont'
import useI18n from '@/hooks/use-i18n'
import { getAllergenCategoryId } from '@/utils/urls'
import AllergenTable from './AllergenTable/AllergenTable'

const ALLERGEN_COLUMNS = [
  { key: 'dairy', i18nKey: 'allergenDairy' },
  { key: 'egg', i18nKey: 'allergenEgg' },
  { key: 'fish', i18nKey: 'allergenFish' },
  { key: 'gluten', i18nKey: 'allergenGluten' },
  { key: 'peanuts', i18nKey: 'allergenPeanuts' },
  { key: 'sesame', i18nKey: 'allergenSesame' },
  { key: 'shellfish', i18nKey: 'allergenShellfish' },
  { key: 'soy', i18nKey: 'allergenSoy' },
  { key: 'treenuts', i18nKey: 'allergenTreenuts' },
] as const

const AllergenGuide = ({
  className,
  sectionBackground,
  disclaimer,
  legendText,
  categories,
}: SanityAllergenGuide) => {
  const { i18n } = useI18n()

  // A category with no items is omitted from the table and from the navigation.
  const visibleCategories = (categories ?? []).filter(category => category.items?.length)

  const disclaimerText = disclaimer || (i18n('menuAllergenText') as string)
  const legend = legendText || (i18n('allergenGuideLegend') as string)

  return (
    <div className={classnames(styles.AllergenGuide, className)}>
      <div className={styles.inner}>
        {visibleCategories.length ? (
          <ContentWithStickyNavigation
            sectionBackground={sectionBackground}
            lineItems={visibleCategories.map(category => ({
              title: category.categoryReference.title,
              id: getAllergenCategoryId(category.categoryReference.slug.current),
            }))}
            className={styles.contentWithStickyNavigation}
            contentGroupQuerySelector="[data-allergen-category]"
            widerNav
            bottomNavContent={
              <div className={styles.bottomNavContent}>
                <p className={styles.disclaimer}>
                  <Widont text={disclaimerText} />
                </p>
                <p className={styles.legend}>{legend}</p>
              </div>
            }
          >
            <AllergenTable
              className={styles.table}
              categories={visibleCategories}
              columns={ALLERGEN_COLUMNS.map(column => ({ key: column.key, label: i18n(column.i18nKey) as string }))}
              labels={{
                caption: i18n('allergenGuideCaption') as string,
                itemColumn: i18n('allergenItemColumn') as string,
                contains: i18n('allergenContains') as string,
                modifiable: i18n('allergenCanBeModified') as string,
              }}
            />
          </ContentWithStickyNavigation>
        ) : (
          <p className={styles.disclaimer}>{disclaimerText}</p>
        )}
      </div>
    </div>
  )
}

AllergenGuide.displayName = 'AllergenGuide'

export default AllergenGuide
